import { Theme } from '@emotion/react';
import styled, { CreateStyled } from '@emotion/styled';
import deepmerge from 'deepmerge';
import { CSSObjectWithLabel, StylesConfig as ReactSelectStyles } from 'react-select';
import { ThemeConfig as ReactSelectThemeConfig } from 'react-select/dist/declarations/src/theme';
import default404Image from '../ISO/Components/UI/Images/Default404Image';
import { ScssVars } from './ScssVars';

declare module '@emotion/react' {
  export interface Theme {
    bootstrap: ScssVars;
    reactSelectTheme: ReactSelectThemeConfig;
    reactSelectStyles: ReactSelectStyles;
    useCalenderIcon: boolean;
    mobileBreakpoint: number;
    searchCriteriaDisplay: {
      color: string;
      backgroundColor: string;
    };
    paxSelectionOptions: {
      withIcon: boolean;
      shortTitle: boolean;
      longTitle?: boolean;
      staticTitle?: boolean;
      displayStaticTitleOnlyBeforeSelection?: boolean;
      validations?: {
        adultsRequired?: boolean;
        childrenRequired?: boolean;
      };
    };
    dateRangePickerOptions?: {
      defaultDaysAddedToStart?: number;
      defaultDaysAddedToEnd?: number;
    };
    placeholderImages: {
      hotel: string;
      room: string;
      flight: string;
      extra: string;
      dining: string;
      rentalCar: string;
      transfer: string;
      package: string;
      camper: string;
    };
    images?: {
      logo: string;
      home: string;
      hotel: string;
    };
    airlineLogoPlaceholder?: string;
    seatMapCategoriesColors?: Record<string, string>;
  }
}

export type CustomTheme = Partial<Theme> & { bootstrap: ScssVars };

export const createTheme = (customTheme: CustomTheme): Theme => {
  const scssVars = customTheme.bootstrap;

  const reactSelectTheme: ReactSelectThemeConfig = theme => ({
    ...theme,
    colors: {
      ...theme.colors,
      ...{
        primary: scssVars.inputFocusBorderColor,
        neutral20: scssVars.inputBorderColor
      }
    }
  });

  const reactSelectStyles: ReactSelectStyles = {
    valueContainer: provided =>
      ({
        ...provided,
        padding: '0 0.75rem'
      } as CSSObjectWithLabel),
    singleValue: provided =>
      ({
        ...provided,
        color: scssVars.inputColor
      } as CSSObjectWithLabel),
    option: (provided, { isSelected, isFocused }) =>
      ({
        ...provided,
        cursor: 'pointer',
        color: isSelected ? scssVars.white : scssVars.inputColor,
        // eslint-disable-next-line no-nested-ternary
        backgroundColor: isSelected ? scssVars.gray600 : isFocused ? scssVars.gray200 : undefined
      } as CSSObjectWithLabel),
    control: (provided, state) =>
      ({
        ...provided,
        borderWidth: scssVars.borderWidth,
        boxShadow: state.isFocused ? scssVars.inputFocusBoxShadow : 'none',
        borderColor: scssVars.inputBorderColor,
        minHeight: 'auto'
      } as CSSObjectWithLabel),
    container: provided =>
      ({
        ...provided,
        borderWidth: scssVars.borderWidth,
        boxShadow: 'none'
      } as CSSObjectWithLabel),
    placeholder: provided =>
      ({
        ...provided,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        color: scssVars.primary
      } as CSSObjectWithLabel),
    menuPortal: provided =>
      ({
        ...provided,
        zIndex: 1100
      } as CSSObjectWithLabel)
  };

  const baseTheme = {
    bootstrap: scssVars,
    reactSelectTheme,
    reactSelectStyles,
    useCalenderIcon: false,
    mobileBreakpoint: 1024,
    searchCriteriaDisplay: {
      color: scssVars.primary,
      backgroundColor: scssVars.gray200
    },
    paxSelectionOptions: {
      withIcon: true,
      shortTitle: true,
      staticTitle: false
    },
    placeholderImages: {
      hotel: default404Image,
      room: default404Image,
      flight: default404Image,
      extra: default404Image,
      dining: default404Image,
      rentalCar: default404Image,
      transfer: default404Image,
      package: default404Image,
      camper: default404Image
    },
    images: {
      logo: '',
      home: '',
      hotel: ''
    }
  };

  return { ...deepmerge(baseTheme, customTheme) };
};

export default styled as CreateStyled;
